/* eslint-disable react/no-unstable-nested-components */
import Avatar from "components/core/Avatar";
import SadFaceIcon from "components/icons/SadFaceIcon";
import getTrailingReturns from "components/issuers/trailingReturns/getTrailingReturns";
import classNames from "components/ui/classNames";
import type { IAgendaQuery } from "lib/queries/__generated__/AgendaQuery.generated";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import parseDateYmd from "lib/utils/parseDateYmd";
import Link from "next/link";
import React from "react";
import { ChevronRightIcon } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import { format } from "util";
import {
  EarningsResultsEventDetails,
  PaymentOfDividendsEventDetails,
  PaymentOfStockDividendsEventDetails,
  RightsIssueEventDetails,
  SpinOffEventDetails,
  StockSplitEventDetails,
} from "./EventsCalendarDetails";
import { getEventNameWithEventType } from "./utils";

interface IDailyEventsCalendarProps {
  events?: IAgendaQuery | null;
  isLoading: boolean;
  isLoadingMore: boolean;

  onLoadMoreEvents: () => void;
  onGoToNextAgendaEvent: () => void;
}

export default function DailyEventsCalendar({
  events,
  isLoading,
  isLoadingMore,
  onLoadMoreEvents,
  onGoToNextAgendaEvent,
}: IDailyEventsCalendarProps) {
  const intl = useIntl();
  const trailingReturns = (events?.agenda.nodes || [])
    .map(({ issuer }) => {
      if (
        issuer?.__typename !== "AmericanCompanyIssuer" &&
        issuer?.__typename !== "BrazilianCompanyIssuer" &&
        issuer?.__typename !== "BrazilianRealEstateFundIssuer"
      ) {
        return null;
      }

      return getTrailingReturns(issuer);
    })
    .filter(notUndefinedOrNull);

  return (
    <div className="md:mt-0">
      <ul className="divide-y divide-slate-100 text-sm leading-6 dark:divide-slate-800">
        {events?.agenda.nodes.map(({ _id, eventDate, eventType, issuer, ...event }) => {
          if (
            issuer?.__typename !== "AmericanCompanyIssuer" &&
            issuer?.__typename !== "BrazilianCompanyIssuer" &&
            issuer?.__typename !== "BrazilianRealEstateFundIssuer"
          ) {
            return null;
          }

          const { name, tickerSymbolPrefix } = issuer;
          if (!name || !tickerSymbolPrefix) {
            return null;
          }

          let linkHref;
          let linkAs;
          let issuerCountry: "US" | "BR" | undefined;

          if (issuer.__typename === "AmericanCompanyIssuer") {
            linkHref = "/us/[tickerSymbol]";
            linkAs = format("/us/%s", tickerSymbolPrefix?.toLowerCase());
            issuerCountry = "US";
          }

          if (issuer.__typename === "BrazilianRealEstateFundIssuer") {
            linkHref = "/br/[tickerSymbol]";
            linkAs = format("/br/%s11", tickerSymbolPrefix?.toLowerCase());
            issuerCountry = "BR";
          }

          if (issuer.__typename === "BrazilianCompanyIssuer") {
            linkHref = "/br/[tickerSymbol]";
            linkAs = format(
              "/br/%s",
              (issuer.securities[0]?.trailingReturns?.tickerSymbol || issuer.tickerSymbolPrefix).toLowerCase(),
            );
            issuerCountry = "BR";
          }

          if (event.__typename === "EarningsResultsEvent") {
            linkHref = format("%s/financials", linkAs);
            linkAs = format("%s/financials", linkAs);
          }

          const parsedEventDate = parseDateYmd(eventDate);
          const eventDetails = (
            <>
              <div className="flex items-center">
                <div className="text-xs font-medium uppercase">
                  {event.__typename === "EarningsResultsEvent" && (
                    <EarningsResultsEventDetails
                      areFinancialsUpdated={event.areFinancialsUpdated}
                      issuer={issuer}
                      trailingReturns={trailingReturns}
                    />
                  )}
                </div>
              </div>

              {event.__typename === "PaymentOfDividendsEvent" && (
                <PaymentOfDividendsEventDetails
                  amountPaid={event.amountPaid}
                  excludingDate={event.excludingDate}
                  eventDate={eventDate}
                  issuerCountry={issuerCountry}
                  recordDate={event.recordDate}
                />
              )}

              {event.__typename === "PaymentOfStockDividendsEvent" && (
                <PaymentOfStockDividendsEventDetails excludingDate={event.excludingDate} sharePaid={event.sharePaid} />
              )}

              {event.__typename === "RightsIssueEvent" && (
                <RightsIssueEventDetails
                  recordDate={event.recordDate}
                  eventDate={eventDate}
                  newShare={event.newShare}
                  subscriptionPrice={event.subscriptionPrice}
                  issuerCountry={issuerCountry}
                />
              )}

              {event.__typename === "StockSplitEvent" && (
                <StockSplitEventDetails
                  excludingDate={event.excludingDate}
                  splitFrom={event.splitFrom}
                  splitTo={event.splitTo}
                />
              )}

              {event.__typename === "SpinOffEvent" && (
                <SpinOffEventDetails
                  excludingDate={event.excludingDate}
                  spinFrom={event.spinFrom}
                  spinTo={event.spinTo}
                  numberOfShares={event.numberOfShares}
                  childCompanyName={event.childCompanyName}
                  childCompanyExchangeId={event.childCompanyExchangeId}
                  childCompanySymbol={event.childCompanySymbol}
                />
              )}
            </>
          );

          return (
            <li key={_id} className="group relative py-4 first:pt-0 last:pb-0">
              <Link href={linkHref || ""} as={linkAs} className="block rounded-xl">
                <div className="flex items-center">
                  <div className="flex min-w-0 flex-1 items-center">
                    <div className="group flex w-full min-w-0 items-center gap-4 md:gap-6">
                      <div className="flex items-center space-x-4">
                        <Avatar
                          src={
                            issuer.__typename === "AmericanCompanyIssuer" ||
                            issuer.__typename === "BrazilianCompanyIssuer"
                              ? issuer.niceLogo?.url || null
                              : null
                          }
                          hashValue={issuer._id}
                          alt={issuer.name}
                          size={34}
                          squircle={true}
                        />

                        <div className="group overflow-hidden">
                          <p
                            className={classNames(
                              "line-clamp-1 max-w-52 text-base font-bold",
                              "text-gray-600 group-hover:text-blue-600 dark:text-slate-300 dark:group-hover:text-blue-400",
                            )}
                          >
                            {name}
                          </p>
                          <div className="flex min-w-52 items-center gap-x-1">
                            <div className="text-md flex items-center justify-start tracking-wider text-gray-800 dark:text-slate-200 md:justify-center">
                              <time dateTime={parsedEventDate.toString()}>
                                {parsedEventDate.toLocaleDateString("pt-BR")}
                              </time>
                            </div>
                            {(event.__typename === "PaymentOfDividendsEvent" ||
                              event.__typename === "PaymentOfStockDividendsEvent") && (
                              <>
                                <div className="h-1 w-1 rounded-full bg-gray-800 dark:bg-slate-400" />
                                <div className="text-gray-500 dark:text-slate-400">
                                  <span className="truncate">{event.symbol}</span>
                                </div>
                              </>
                            )}
                            <div className="h-1 w-1 rounded-full bg-gray-800 dark:bg-slate-400" />
                            <div className="text-gray-500 dark:text-slate-400">
                              <span className="truncate">
                                {event.__typename === "EarningsResultsEvent"
                                  ? getEventNameWithEventType(eventType, event.eventFiscalYear, intl.locale)
                                  : getEventNameWithEventType(eventType, null, intl.locale)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="hidden lg:flex">{eventDetails}</div>
                    </div>
                  </div>

                  <div>
                    <ChevronRightIcon className="h-5 w-5 text-gray-400 dark:text-slate-500" aria-hidden="true" />
                  </div>
                </div>
              </Link>

              <div className="p-1 pt-3 lg:hidden">{eventDetails}</div>
            </li>
          );
        })}

        {isLoadingMore && (
          <>
            {Array.from({ length: 3 }).map((_, index) => (
              <li key={`calendar-loading-skeleton-${String(index)}`}>
                <EventItemSkeleton />
              </li>
            ))}
          </>
        )}

        {!isLoading && !events?.agenda.nodes.length && (
          <li className="justify-center space-y-2 rounded-md p-8 text-center text-base tracking-wide text-gray-600 dark:text-slate-400">
            <div>
              <SadFaceIcon />
            </div>

            <p>
              <FormattedMessage defaultMessage="Nenhum evento encontrado." id="wd3BX6" />
            </p>

            {events?.agenda.nextUpcomingEventDateRange && (
              <div>
                <button
                  type="button"
                  className={classNames(
                    "mt-4 rounded-md border bg-white px-4 py-2 text-sm text-gray-500 shadow",
                    "hover:border-transparent hover:bg-brand hover:text-white",
                    "dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300",
                  )}
                  onClick={onGoToNextAgendaEvent}
                >
                  <FormattedMessage defaultMessage="Ir para o próximo evento" id="zAna+x" />
                </button>
              </div>
            )}
          </li>
        )}
      </ul>
      {events?.agenda.pageInfo.hasNextPage && !isLoadingMore && (
        <div className="pt-8 text-center">
          <button
            disabled={isLoading}
            type="button"
            className={classNames(
              "font-aeonik text-base font-medium tracking-wide text-blue-600 hover:underline",
              "disabled:text-gray-600",
              "dark:text-blue-400 dark:disabled:text-slate-400",
            )}
            onClick={onLoadMoreEvents}
          >
            <FormattedMessage defaultMessage="Carregar mais eventos" id="ZVPC3u" />
          </button>
        </div>
      )}
    </div>
  );
}

export function EventItemSkeleton() {
  return (
    <div className="px-4 py-2">
      <div className="flex animate-pulse items-start space-x-4">
        <div className="h-8 w-8 rounded-full bg-slate-200 squircle dark:bg-slate-700" />

        <div className="flex-1 space-y-2">
          <div className="h-3 w-32 rounded bg-slate-300 dark:bg-slate-600" />
          <div className="h-2 w-2/3 rounded bg-slate-200 dark:bg-slate-700" />
          <div className="h-2 w-36 rounded bg-slate-200 dark:bg-slate-700" />
        </div>
      </div>
    </div>
  );
}
